import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { Loader2, RefreshCcw, BadgePercent } from "lucide-react";
import TabPage, { TabItemProps } from "../../components/dynamic/Tab";
import DynamicList from "../../components/dynamic/List";
import { ApiService } from "../../services/api";
import { isYesToolsHostname } from "../../utils/yesTools";

type MenuCategory = {
  id: string;
  name: string;
};

type MenuProduct = {
  id: string;
  name: string;
  description?: string;
  imageUrl?: string;
  basePrice?: number | null;
  priceListPrice?: number | null;
  finalPrice?: number | null;
  hasCampaign?: boolean;
  campaignName?: string | null;
  category?: { id: string; name: string };
};

const formatPrice = (price?: number | null) => {
  if (price === null || price === undefined || Number.isNaN(price)) return "-";

  return new Intl.NumberFormat("tr-TR", {
    style: "currency",
    currency: "TRY",
    minimumFractionDigits: 2,
  }).format(price);
};

function CafeProductCard({ product, viewMode }: { product: MenuProduct; viewMode?: string }) {
  if (viewMode === "grid") {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden hover:bg-gray-100 transition-colors h-full">
        <div className="aspect-square bg-white border-b border-gray-200 overflow-hidden">
          {product.imageUrl ? (
            <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-500 font-semibold text-2xl">
              {product.name.slice(0, 1)}
            </div>
          )}
        </div>

        <div className="p-4 space-y-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">{product.name}</h3>
            <p className="mt-1 text-xs text-gray-500 line-clamp-2">
              {product.description || product.category?.name || "Cafe ürünü"}
            </p>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-3">
            {product.basePrice && product.basePrice !== product.finalPrice ? (
              <div className="text-xs text-gray-400 line-through">{formatPrice(product.basePrice)}</div>
            ) : null}
            <div className="text-lg font-bold text-gray-900">{formatPrice(product.finalPrice)}</div>
          </div>

          {product.hasCampaign && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              <BadgePercent className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span className="line-clamp-2">{product.campaignName || "Aktif kampanya uygulanıyor"}</span>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 sm:p-4 hover:bg-gray-100 transition-colors">
      <div className="flex gap-4">
        <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg bg-white border border-gray-200 overflow-hidden flex-shrink-0">
          {product.imageUrl ? (
            <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-500 font-semibold text-xl">
              {product.name.slice(0, 1)}
            </div>
          )}
        </div>

        <div className="flex-1 min-w-0 flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900">{product.name}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {product.description || product.category?.name || "Cafe ürünü"}
            </p>
            {product.hasCampaign && (
              <div className="mt-3 inline-flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <BadgePercent className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>{product.campaignName || "Aktif kampanya uygulanıyor"}</span>
              </div>
            )}
          </div>

          <div className="w-full md:w-52 bg-white border border-gray-200 rounded-lg p-3">
            {product.basePrice && product.basePrice !== product.finalPrice ? (
              <div className="text-xs text-gray-400 line-through">{formatPrice(product.basePrice)}</div>
            ) : null}
            <div className="text-xl font-bold text-gray-900">{formatPrice(product.finalPrice)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function CafeCategoryPanel({
  categoryId,
}: {
  categoryId?: string;
}) {
  const [refreshKey, setRefreshKey] = useState(0);
  const actions = useMemo(
    () => (
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setRefreshKey((prev) => prev + 1)}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 text-sm font-medium"
        >
          <RefreshCcw className="w-4 h-4" />
          Yenile
        </button>
      </div>
    ),
    []
  );

  const fetcher = useCallback(
    async (_page: number, searchTerm?: string) => {
      const response = await ApiService.getCafeMenuCatalog({
        categoryId,
        search: searchTerm || undefined,
        limit: 200,
        offset: 0,
      });

      return {
        data: response?.data?.products || [],
        meta: {
          page: 1,
          totalPages: 1,
          totalItems: (response?.data?.products || []).length,
          limit: 200,
        },
      };
    },
    [categoryId]
  );

  return (
    <DynamicList
      type="list"
      header={false}
      title="Cafe Menüsü"
      path={`cafe-menu-${categoryId || "all"}`}
      fetcher={fetcher}
      refreshKey={refreshKey}
      isSearch
      isPagination={false}
      showToggle
      actions={actions}
      emptyMessage="Bu kategoride gösterilecek ürün bulunamadı."
      errorMessage="Cafe menüsü yüklenirken bir hata oluştu."
      item={(product: MenuProduct, viewMode?: string) => (
        <CafeProductCard key={product.id} product={product} viewMode={viewMode} />
      )}
    />
  );
}

export default function CafeMenuPage() {
  const isYesToolsDomain =
    typeof window !== "undefined" && isYesToolsHostname(window.location.hostname);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [categories, setCategories] = useState<MenuCategory[]>([]);

  const loadCategories = useCallback(async () => {
    try {
      setLoading(true);
      setError("");

      const response = await ApiService.getCafeMenuCatalog({
        limit: 200,
        offset: 0,
      });

      setCategories(Array.isArray(response?.data?.categories) ? response.data.categories : []);
    } catch (err: any) {
      console.error("Cafe menüsü sekmeleri yüklenirken hata:", err);
      setError(err?.message || "Cafe menüsü yüklenemedi.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isYesToolsDomain) {
      loadCategories();
    }
  }, [isYesToolsDomain, loadCategories]);

  const tabs = useMemo<TabItemProps[]>(() => {
    const result: TabItemProps[] = [
      {
        type: "custom",
        title: "Tümü",
        path: "all",
        tabId: "all",
        customComponent: <CafeCategoryPanel />,
      },
    ];

    categories.forEach((category) => {
      result.push({
        type: "custom",
        title: category.name,
        path: category.id,
        tabId: category.id,
        customComponent: <CafeCategoryPanel categoryId={category.id} />,
      });
    });

    return result;
  }, [categories]);

  if (!isYesToolsDomain) {
    return <Navigate to="/dashboard" replace />;
  }

  if (loading) {
    return (
      <div className="h-[calc(100dvh-4rem)] bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-[calc(100dvh-4rem)] bg-gray-50 p-6">
        <div className="max-w-5xl mx-auto bg-white rounded-xl shadow border border-red-200 p-6 text-red-600">
          {error}
        </div>
      </div>
    );
  }

  return <TabPage title="Cafe Menüsü" tabs={tabs} />;
}
